// ============================================================================
// DRAW MERGE SORT CLASSIC TREE
// Canvas drawing routine for the classic multi-level merge sort visualization.
// ============================================================================

import type { ExecutionStep } from '../../../../core/types/rust-engine-interfaces';
import {
  type MergeTreeLayout,
  type MergeTreeNode,
  buildMergeTree,
  findActiveNode,
  nodeKey,
  updateTreeValues,
} from './mergeSortTree';

const COLORS = {
  blockDefault: 'rgba(59, 130, 246, 0.85)',
  blockDefaultBorder: '#60a5fa',
  blockActive: 'rgba(245, 158, 11, 0.85)',
  blockActiveBorder: '#f59e0b',
  blockMerged: 'rgba(34, 197, 94, 0.85)',
  blockMergedBorder: '#22c55e',
  text: '#ffffff',
  arrow: '#94a3b8',
  arrowActive: '#f59e0b',
  label: '#cbd5e1',
};

let cachedLayout: MergeTreeLayout | null = null;
let cachedN = -1;
let visitedNodes: Set<string> = new Set();
let lastStepIndex = -1;

export function drawMergeTree(
  ctx: CanvasRenderingContext2D,
  currentStep: ExecutionStep,
  W: number,
  H: number,
  stepIndex?: number,
): void {
  if (currentStep.payload.type !== 'array') return;
  const items = currentStep.payload.items;
  const values = items.map(i => Number(i.value) || 0);
  const n = values.length;
  if (n === 0) return;

  // Reuse the tree structure across frames — only values/states change.
  if (!cachedLayout || cachedN !== n) {
    cachedLayout = buildMergeTree(n);
    cachedN = n;
    visitedNodes = new Set();
    // Root is always visible from the start.
    visitedNodes.add(nodeKey(0, n - 1));
  }
  // Reset progressive reveal on Initialization (replay / restart with same n).
  if (currentStep.stepType === 'initialization') {
    visitedNodes = new Set();
    visitedNodes.add(nodeKey(0, n - 1));
  }
  // Reset on backward scrub — visited nodes only grow forward.
  if (stepIndex !== undefined && stepIndex < lastStepIndex) {
    visitedNodes = new Set();
    visitedNodes.add(nodeKey(0, n - 1));
  }
  if (stepIndex !== undefined) lastStepIndex = stepIndex;
  const layout = cachedLayout;
  const activeNode = findActiveNode(layout, currentStep);
  updateTreeValues(layout, values, activeNode);

  // Progressive reveal: mark the active node and its children as visited.
  if (activeNode) {
    const key = nodeKey(activeNode.range[0], activeNode.range[1]);
    visitedNodes.add(key);
    // When a node is visited, reveal its children (the split halves).
    for (const child of activeNode.children) {
      visitedNodes.add(nodeKey(child.range[0], child.range[1]));
    }
    // Also reveal all ancestors — they've been split already.
    let p = activeNode.parent;
    while (p) {
      visitedNodes.add(nodeKey(p.range[0], p.range[1]));
      p = p.parent;
    }
  }
  // On Completion step, reveal everything.
  if (currentStep.stepType === 'completion') {
    for (const node of layout.nodes) {
      visitedNodes.add(nodeKey(node.range[0], node.range[1]));
    }
  }

  const isNarrow = W < 480;
  const PAD_X = isNarrow ? 12 : 24;
  const PAD_Y = isNarrow ? 20 : 28;
  const BOTTOM_LABEL_H = 18;
  const availableH = H - PAD_Y * 2 - BOTTOM_LABEL_H;
  const availableW = W - PAD_X * 2;

  const rowCount = layout.maxDepth + 1;
  const rowGap = Math.max(isNarrow ? 16 : 24, (availableH - rowCount * 32) / Math.max(1, rowCount - 1));
  const rowH = 32;

  // Compute x positions for each node using in-order leaf-based layout.
  assignXPositions(layout, availableW, PAD_X);

  // Draw arrows from parents to children first (so they appear behind blocks).
  drawArrows(ctx, layout, PAD_Y, rowH, rowGap, activeNode, visitedNodes);

  // Draw every node block — but only if it has been visited (progressive reveal).
  for (const node of layout.nodes) {
    const key = nodeKey(node.range[0], node.range[1]);
    if (!visitedNodes.has(key)) continue;
    const rowY = PAD_Y + node.depth * (rowH + rowGap);
    drawNodeBlock(ctx, node, rowY, isNarrow);
  }

  // Bottom label.
  ctx.font = `bold ${isNarrow ? 9 : 11}px monospace`;
  ctx.fillStyle = COLORS.label;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillText('Merge Sort: divide (top-down) → merge (bottom-up)', PAD_X, H - 4);
}

/** Assign x positions to each node by aligning leaf positions with array indices. */
function assignXPositions(layout: MergeTreeLayout, availW: number, padX: number): void {
  const n = layout.root.range[1] - layout.root.range[0] + 1;
  const leafNodes = layout.nodes.filter(n => n.children.length === 0).sort((a, b) => a.range[0] - b.range[0]);
  if (leafNodes.length !== n) return;

  // Determine leaf block width based on available width.
  const maxBlockW = 48;
  const gap = 4;
  const minLeafW = availW < 432 ? 14 : 20;
  const leafW = Math.max(minLeafW, Math.min(maxBlockW, (availW - (n - 1) * gap) / n));
  const totalLeafW = n * leafW + (n - 1) * gap;
  const startX = padX + Math.max(0, (availW - totalLeafW) / 2);

  for (let i = 0; i < leafNodes.length; i++) {
    const leaf = leafNodes[i]!;
    const [L, R] = leaf.range;
    const leafCount = R - L + 1;
    const leafCenterX = startX + i * (leafW + gap) + leafW / 2;
    // This leaf's block width matches how many original indices it covers.
    leaf.x = leafCenterX - (leafCount * leafW + (leafCount - 1) * gap) / 2;
    leaf.width = leafCount * leafW + (leafCount - 1) * gap;
  }

  // Propagate x positions up the tree: parent is centered over its children.
  const assignParent = (node: MergeTreeNode) => {
    if (node.children.length === 0) return;
    for (const child of node.children) assignParent(child);
    const first = node.children[0]!;
    const last = node.children[node.children.length - 1]!;
    node.width = last.x + last.width - first.x;
    node.x = first.x;
  };
  assignParent(layout.root);
}

function drawArrows(
  ctx: CanvasRenderingContext2D,
  layout: MergeTreeLayout,
  padY: number,
  rowH: number,
  rowGap: number,
  activeNode: MergeTreeNode | null,
  visited: Set<string>,
): void {
  ctx.save();
  ctx.lineWidth = 1.5;
  for (const node of layout.nodes) {
    if (node.children.length === 0) continue;
    const parentKey = nodeKey(node.range[0], node.range[1]);
    if (!visited.has(parentKey)) continue;
    const parentY = padY + node.depth * (rowH + rowGap) + rowH;
    const isOnActivePath = activeNode && isAncestorOf(node, activeNode);
    ctx.strokeStyle = isOnActivePath ? COLORS.arrowActive : COLORS.arrow;
    for (const child of node.children) {
      const childKey = nodeKey(child.range[0], child.range[1]);
      if (!visited.has(childKey)) continue;
      const childY = padY + child.depth * (rowH + rowGap);
      const startX = node.x + node.width / 2;
      const endX = child.x + child.width / 2;
      ctx.beginPath();
      ctx.moveTo(startX, parentY + 2);
      ctx.lineTo(endX, childY - 2);
      ctx.stroke();

      // Small arrowhead at child side.
      ctx.fillStyle = isOnActivePath ? COLORS.arrowActive : COLORS.arrow;
      const angle = Math.atan2(childY - parentY - 4, endX - startX);
      ctx.beginPath();
      ctx.moveTo(endX, childY - 2);
      ctx.lineTo(endX - 5 * Math.cos(angle - 0.4), childY - 2 - 5 * Math.sin(angle - 0.4));
      ctx.lineTo(endX - 5 * Math.cos(angle + 0.4), childY - 2 - 5 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

function isAncestorOf(ancestor: MergeTreeNode, descendant: MergeTreeNode): boolean {
  let p: MergeTreeNode | null = descendant.parent;
  while (p) {
    if (p === ancestor) return true;
    p = p.parent;
  }
  return false;
}

function drawNodeBlock(ctx: CanvasRenderingContext2D, node: MergeTreeNode, y: number, isNarrow: boolean): void {
  const blockW = node.width;
  const blockH = 28;
  const x = node.x;
  const values = node.values;
  const m = values.length;
  const cellW = m > 0 ? blockW / m : blockW;

  let fillColor = COLORS.blockDefault;
  let borderColor = COLORS.blockDefaultBorder;
  if (node.state === 'active') {
    fillColor = COLORS.blockActive;
    borderColor = COLORS.blockActiveBorder;
  } else if (node.state === 'merged') {
    fillColor = COLORS.blockMerged;
    borderColor = COLORS.blockMergedBorder;
  } else if (node.state === 'sorted') {
    fillColor = COLORS.blockMerged;
    borderColor = COLORS.blockMergedBorder;
  }

  ctx.save();
  // Block background
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.roundRect(x, y, blockW, blockH, 4);
  ctx.fill();

  // Border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = node.state === 'active' ? 2.5 : 1.5;
  ctx.beginPath();
  ctx.roundRect(x, y, blockW, blockH, 4);
  ctx.stroke();

  // Inner cell dividers
  ctx.strokeStyle = 'rgba(0,0,0,0.2)';
  ctx.lineWidth = 0.5;
  for (let i = 1; i < m; i++) {
    const cx = x + i * cellW;
    ctx.beginPath();
    ctx.moveTo(cx, y + 2);
    ctx.lineTo(cx, y + blockH - 2);
    ctx.stroke();
  }

  // Values
  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${isNarrow ? 10 : 12}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 0; i < m; i++) {
    const cx = x + (i + 0.5) * cellW;
    ctx.fillText(String(values[i]), cx, y + blockH / 2);
  }

  ctx.restore();
}

